import React, { useState } from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CheckCircle,
  ContentCopy,
  Crop,
  DeleteOutline,
  DragIndicator,
  ExpandMore,
  InsertDriveFileOutlined,
  WarningAmber,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";

import MediaDisplay from "@/common/MediaDisplay";
import { MEDIA_DRAG } from "@/components/alerts/add-v2/model/dropIntent";
import { imageTagsAsText } from "@/components/alerts/add-v2/model/imageTags";
import { BulletinMediaItem } from "@/components/alerts/add-v2/model/types";

import { AttachMenu, AttachOption } from "./AttachMenu";

/**
 * One uploaded file: the thumbnail, the filename over it, and the entities it belongs to.
 *
 * Two ways to attach, both required by the design: drag the file onto an entity card, or pick from
 * the "In this media" list. The drag is the discoverable one and the picker is the one that works
 * on a phone, with a collapsed entities section, or with a keyboard.
 *
 * ## Dragging it
 *
 * **The handle at the top-left is the only thing that drags**, and one drag does both jobs: dropped
 * on an entity card the file attaches, dropped on another media card it moves there. Where it lands
 * decides what it meant — `model/dropIntent.ts` is the one place that decides.
 *
 * The card body deliberately does *not* drag. It held the same gesture until 2026-08-19 and it made
 * the card a thing you could not click, select text in, or scroll past on a touch screen without
 * starting to move a file.
 *
 * dnd-kit, in the page's context, since 2026-08-21. It was HTML5 drag-and-drop, which needs no
 * context to cross into the entities section — and fires **nothing** for touch or the keyboard, so
 * a phone could not reorder at all. The handle is a real button again for the same reason: with a
 * `KeyboardSensor` in the context, space picks a card up and the arrow keys move it.
 */

interface MediaRowProps {
  item: BulletinMediaItem;
  /** Every entity on the bulletin, `checked` where this file is already attached. */
  entityOptions: AttachOption[];
  disabled?: boolean;
  onDelete: () => void;
  onToggleEntity: (entityId: string) => void;
  /** The thumbnail was clicked: show the file at full size. Absent for a file nothing can show. */
  onExpand?: () => void;
  /** Crop and rotate. Absent for anything that is not an image — there is nothing to crop. */
  onEdit?: () => void;
}

export const MediaRow: React.FC<MediaRowProps> = ({
  item,
  entityOptions,
  disabled = false,
  onDelete,
  onToggleEntity,
  onExpand,
  onEdit,
}) => {
  const { t } = useTranslation("bulletin/create-v2");
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [tagsOpen, setTagsOpen] = useState(false);
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: item.id, data: MEDIA_DRAG, disabled });

  const isVideo = item.contentType.includes("video");
  const hasTags = item.tagGroups.length > 0;
  const isImage = item.contentType.includes("image");
  const attached = entityOptions.filter((option) => option.checked);

  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      style={{ transform: CSS.Translate.toString(transform), transition }}
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        overflow: "visible",
        minWidth: 0,
        borderColor: isOver && !isDragging ? "primary.main" : undefined,
        // The card being dragged passes over opaque neighbours, so it has to sit above them.
        position: "relative",
        zIndex: isDragging ? 1 : 0,
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: 140,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "action.hover",
          borderRadius: "8px 8px 0 0",
          overflow: "hidden",
        }}
      >
        {isImage || isVideo ? (
          // A button around the thumbnail alone, not the card: the card holds three other controls,
          // and a button inside a button is not something a browser or a screen reader can make
          // sense of. Everything else on the card sits outside this one.
          <Box
            component="button"
            type="button"
            onClick={onExpand}
            aria-label={t("media.expand_media", { filename: item.filename })}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              p: 0,
              border: 0,
              background: "none",
              cursor: "zoom-in",
            }}
          >
            <MediaDisplay
              mediaType={isVideo ? "video" : "image"}
              // A video has no thumbnail — the poster frame comes from the file itself.
              src={isVideo ? item.url : (item.thumbnailUrl ?? item.url)}
              style={{
                maxWidth: "100%",
                maxHeight: "140px",
                objectFit: "contain",
                // `MediaDisplay` renders a video with its own controls, which would swallow the
                // click and play it in a 140px box. **Video plays in the lightbox**, where it has
                // room and the same controls — the card is a thumbnail either way.
                pointerEvents: "none",
              }}
            />
          </Box>
        ) : (
          <InsertDriveFileOutlined
            sx={{ fontSize: 36, color: "action.active" }}
          />
        )}

        {/*
          The drag handle, and the whole of what can be dragged. A real button now: dnd-kit's
          `KeyboardSensor` makes it operable, so it belongs in the tab order — under HTML5 drag it
          was `tabIndex={-1}` because pressing it could not have done anything.
        */}
        <IconButton
          ref={setActivatorNodeRef}
          size="small"
          disabled={disabled}
          {...attributes}
          {...listeners}
          aria-label={t("media.drag_media", { filename: item.filename })}
          sx={{
            position: "absolute",
            top: 2,
            left: 2,
            cursor: disabled ? "default" : "grab",
            touchAction: "none",
            backgroundColor: "rgba(255,255,255,0.7)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" },
          }}
        >
          <DragIndicator fontSize="small" />
        </IconButton>

        <IconButton
          size="small"
          disabled={disabled}
          aria-label={t("media.remove_media", { filename: item.filename })}
          onClick={onDelete}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            backgroundColor: "rgba(255,255,255,0.7)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" },
          }}
        >
          <DeleteOutline fontSize="small" />
        </IconButton>

        {isImage && onEdit && (
          <IconButton
            size="small"
            disabled={disabled}
            aria-label={t("media.edit_media", { filename: item.filename })}
            onClick={onEdit}
            sx={{
              // Under Delete, not beside it: the top-left corner is the drag handle and the
              // bottom-right is the filename, so the right edge is the one free column.
              position: "absolute",
              top: 40,
              right: 4,
              backgroundColor: "rgba(255,255,255,0.7)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" },
            }}
          >
            <Crop fontSize="small" />
          </IconButton>
        )}

        <Typography
          variant="caption"
          title={item.filename}
          sx={{
            position: "absolute",
            bottom: 6,
            right: 6,
            maxWidth: "calc(100% - 24px)",
            px: 0.75,
            borderRadius: 1,
            backgroundColor: "rgba(255,255,255,0.75)",
            color: "text.secondary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.filename}
        </Typography>
      </Box>

      <Box sx={{ p: 1.25, pt: 1.75 }}>
        <Box sx={{ position: "relative" }}>
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              top: -9,
              left: 10,
              px: 0.5,
              zIndex: 1,
              backgroundColor: "background.paper",
              color: "text.secondary",
            }}
          >
            {t("media.in_this_media")}
          </Typography>

          {/*
            Presentational, despite the click. It used to carry `role="button"`, which put the
            chips' delete buttons inside a button — and a control nested in a button is
            announced as presentational by some screen readers, so detaching an entity became
            unreachable. The keyboard and assistive-technology path is the trigger below,
            which is a real button; this handler only keeps "click anywhere in the field"
            working for the mouse.
          */}
          {}
          <Box
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={{
              position: "relative",
              minHeight: 40,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 0.5,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              pl: 1,
              pr: 3.5,
              py: 0.75,
              cursor: "pointer",
              "&:hover": { borderColor: "text.primary" },
            }}
          >
            {attached.length === 0 ? (
              <Typography variant="caption" color="text.disabled">
                {t("media.select_entities")}
              </Typography>
            ) : (
              attached.map((option) => (
                <Chip
                  key={option.id}
                  size="small"
                  icon={option.icon as React.ReactElement}
                  label={option.label}
                  onDelete={(event) => {
                    event.stopPropagation();
                    onToggleEntity(option.id);
                  }}
                  sx={{ maxWidth: "100%" }}
                />
              ))
            )}
            {/*
              The real control: what a keyboard tabs to and what a screen reader announces,
              now a sibling of the chips rather than their ancestor. `stopPropagation` keeps
              the container's mouse handler from re-opening what this just toggled.
            */}
            <IconButton
              size="small"
              aria-label={t("media.in_this_media")}
              aria-haspopup="listbox"
              aria-expanded={Boolean(menuAnchor)}
              onClick={(event) => {
                event.stopPropagation();
                setMenuAnchor(event.currentTarget);
              }}
              sx={{
                position: "absolute",
                right: 2,
                top: "50%",
                transform: "translateY(-50%)",
                p: 0.25,
              }}
            >
              <ExpandMore sx={{ fontSize: 18, color: "text.secondary" }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/*
        The generated description, back on every image card as of the 2026-08-21 design.

        It is **read-only and it is not for the officer's benefit** — the notice says so in the
        design's own words: these tags exist to make the image findable, and the bulletin's readers
        never see them. What the officer needs from the panel is the two things production always
        showed: that the tags came back, and that they did not.

        There is no "generating…" state, unlike the prototype: the backend generates the tags inside
        the upload request, so by the time a card exists its tags are final. There is no retry
        either — the 2026-07-28 review asked for one and no endpoint has ever existed.
      */}
      {isImage && (
        <Box
          sx={{
            borderTop: 1,
            borderColor: "divider",
            px: 1.25,
            pt: 1,
            pb: tagsOpen ? 1.25 : 0.75,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              component="button"
              type="button"
              variant="caption"
              onClick={() => setTagsOpen((open) => !open)}
              aria-expanded={tagsOpen}
              sx={{
                flex: 1,
                minWidth: 0,
                textAlign: "left",
                border: 0,
                background: "none",
                p: 0,
                font: "inherit",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {t("media.tags_title")}
            </Typography>

            {hasTags ? (
              <>
                <IconButton
                  size="small"
                  aria-label={t("media.tags_copy")}
                  onClick={() => {
                    // `writeText` rejects when the document is not focused, the context is
                    // not secure, or permission is denied. Reporting success before the
                    // promise settles tells the officer they have something they do not.
                    navigator.clipboard
                      .writeText(imageTagsAsText(item.tagGroups))
                      .then(() => toast.success(t("media.tags_copied")))
                      .catch(() => toast.error(t("media.tags_copy_failed")));
                  }}
                >
                  <ContentCopy sx={{ fontSize: 15 }} />
                </IconButton>
                <CheckCircle
                  titleAccess={t("media.tags_generated")}
                  sx={{ fontSize: 16, color: "text.primary" }}
                />
              </>
            ) : (
              <WarningAmber
                titleAccess={t("media.tags_missing")}
                sx={{ fontSize: 16, color: "text.primary" }}
              />
            )}

            <ExpandMore
              sx={{
                fontSize: 18,
                color: "text.secondary",
                transform: tagsOpen ? "rotate(180deg)" : "none",
                transition: "transform .15s",
              }}
            />
          </Box>

          <Collapse in={tagsOpen} unmountOnExit>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mt: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  p: 1,
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  backgroundColor: hasTags ? "action.hover" : "warning.light",
                  color: "text.primary",
                  whiteSpace: "pre-line",
                }}
              >
                {t(hasTags ? "media.tags_notice" : "media.tags_notice_missing")}
              </Typography>

              {item.tagGroups.map((group) => (
                <Box key={group.label || group.tags}>
                  {group.label && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontWeight: 700,
                        letterSpacing: ".5px",
                      }}
                    >
                      {group.label}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ display: "block" }}>
                    {group.tags}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}

      <AttachMenu
        anchorEl={menuAnchor}
        options={entityOptions}
        emptyLabel={t("media.no_entities")}
        onToggle={onToggleEntity}
        onClose={() => setMenuAnchor(null)}
      />
    </Paper>
  );
};
